/* @jsxImportSource react */
/* Vite's react plugin already compiles JSX with react/jsx-runtime; the pragma keeps
   that true under Playwright's babel transform too, so the node-side unit tests
   can render these components with react-dom/server. */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import { alerts as alertsApi, overview } from '../api/client';
import type { AlertsView, BucketUsage, OverviewJob, OverviewView, SyncBucketState, VerifyDay, Zone } from '../api/types';
import { OTHER_PROJECT, PROJECT_META, PROJECT_ORDER } from '../api/types';
import { formatAge, formatBytes, formatTs } from '../lib/format';
import { Badge, EmptyState, ErrorState, Spinner, StatusPill } from '../components/ui';
import { ZONE_META, ZONE_ORDER, ZoneBadge } from '../components/ZoneBadge';

/** Zone tally for the header strip: "RU 7 · KZ-DR 4 · VM 2 · GH 2 · 💻 1". */
export function ZoneTally({ jobs }: { jobs: Array<{ zone: Zone }> }) {
  const counts = new Map<Zone, number>();
  for (const job of jobs) counts.set(job.zone, (counts.get(job.zone) ?? 0) + 1);
  if (counts.size === 0) return null;
  return (
    <span className="zone-tally header-strip__label" data-testid="zone-tally" title="джобы по зонам исполнения">
      {ZONE_ORDER.filter((zone) => counts.has(zone)).map((zone) => (
        <span key={zone} title={ZONE_META[zone]?.title}>
          {ZONE_META[zone]?.short ?? zone} {counts.get(zone)}
        </span>
      ))}
    </span>
  );
}

/** Header strip: the four numbers that orient an operator in three seconds. */
export function HeaderStrip({ data }: { data: OverviewView }) {
  const runner = data.runner;
  return (
    <div className="header-strip" role="status" aria-label="Overall state" data-testid="header-strip">
      <StatusPill status={data.overall} title={`overall, ${data.generated_at}`} />
      <ZoneTally jobs={data.jobs} />
      <span className="header-strip__label">
        runner:{' '}
        {runner.offline ? (
          <strong className="header-strip__label">offline</strong>
        ) : (
          <>
            {runner.last_poll_age_s === null ? 'never polled' : `${formatAge(runner.last_poll_age_s)} назад`}
            {` · в очереди ${runner.queue_depth} · inflight ${runner.inflight}`}
          </>
        )}
      </span>
      <span className="header-strip__label">freshness sweep: {formatTs(data.sweeps.fresh_ts)}</span>
      <span className="header-strip__label">
        sync: <StatusPill status={data.sync.state} title={data.sync.overdue.join(', ')} />{' '}
        {data.sync.lag_hours !== null ? `lag ${data.sync.lag_hours} ч` : ''}
      </span>
      <span className="header-strip__label muted">обновлено {formatTs(data.generated_at)}</span>
    </div>
  );
}

/** Metrics strip (W4): success rate 7d, RPO compliance, bucket usage vs quota. */
export function MetricsStrip({ metrics }: { metrics: OverviewView['metrics'] }) {
  const rate = metrics.success_rate_7d;
  const rpo = metrics.rpo_compliance;
  const verify = metrics.restore_verify;
  return (
    <div className="metrics-strip" data-testid="metrics-strip">
      <span className="header-strip__label">
        success 7d:{' '}
        <strong>{rate.pct === null ? '—' : `${rate.pct.toFixed(0)}%`}</strong>
        <span className="muted"> ({rate.ok}/{rate.total})</span>
      </span>
      <span className="header-strip__label">
        RPO compliance:{' '}
        <strong>{rpo.pct === null ? '—' : `${rpo.pct.toFixed(0)}%`}</strong>
        <span className="muted"> ({rpo.within}/{rpo.total})</span>
      </span>
      <span className="header-strip__label">
        restore-verify (7d):{' '}
        {verify.board.some((day) => day.runs > 0) ? (
          <>
            <strong className="verify-ok">{verify.board.reduce((sum, day) => sum + day.ok, 0)}</strong>
            <span className="muted"> ok · </span>
            <strong className={verify.board.some((day) => day.fail > 0) ? 'verify-fail' : undefined}>
              {verify.board.reduce((sum, day) => sum + day.fail, 0)}
            </strong>
            <span className="muted"> fail · {verify.missed_days} missed</span>
          </>
        ) : (
          <span className="muted">нет прогонов</span>
        )}
      </span>
    </div>
  );
}

/** Usage bar per bucket: % of quota (unlimited buckets show the size only). */
export function UsageBars({ usage }: { usage: BucketUsage[] }) {
  if (usage.length === 0) {
    return <EmptyState title="Занятость бакетов ещё не измерена" hint="Появится после первого свипа/замера usage." />;
  }
  return (
    <div className="usage-list" data-testid="usage-bars">
      {usage.map((bucket) => (
        <div key={bucket.bucket} className="usage-row">
          <span className="usage-row__name" title={bucket.partial ? 'оценка по freshness-свипу (бакет не зеркалится)' : undefined}>
            {bucket.bucket}
            {bucket.partial ? ' *' : ''}
          </span>
          <span className="usage-row__bar" aria-hidden="true">
            <span
              className={`usage-bar${(bucket.kzPct ?? bucket.ruPct ?? 0) >= 80 ? ' usage-bar--hot' : ''}`}
              style={{ width: `${Math.min(100, Math.max(0, bucket.kzPct ?? bucket.ruPct ?? 0))}%` }}
            />
          </span>
          <span className="usage-row__num muted">
            {quotaLabel(bucket)}
          </span>
        </div>
      ))}
    </div>
  );
}

function quotaLabel(bucket: BucketUsage): string {
  const sides: string[] = [];
  if (bucket.ruBytes !== null || bucket.ruPct !== null) {
    sides.push(`RU ${formatBytes(bucket.ruBytes)}${bucket.ruPct === null ? '' : ` (${bucket.ruPct.toFixed(0)}%)`}`);
  }
  if (bucket.kzBytes !== null || bucket.kzPct !== null) {
    sides.push(`KZ ${formatBytes(bucket.kzBytes)}${bucket.kzPct === null ? '' : ` / ${formatBytes(bucket.kzQuotaBytes)} (${bucket.kzPct.toFixed(0)}%)`}`);
  }
  return sides.length === 0 ? '—' : sides.join(' · ');
}

/** Restore-verify mini-board: 7 days, one cell per day. */
export function VerifyBoard({ board, job }: { board: VerifyDay[]; job: string }) {
  return (
    <div className="verify-board" data-testid="verify-board" aria-label={`restore-verify board, job ${job}`}>
      {board.map((day) => (
        <span
          key={day.day}
          className={`verify-cell ${day.fail > 0 ? 'verify-cell--fail' : day.ok > 0 ? 'verify-cell--ok' : 'verify-cell--miss'}`}
          title={`${day.day}: ${day.runs === 0 ? 'нет прогона' : `${day.ok} ok / ${day.fail} fail`}${day.lastNote ? ` — ${day.lastNote}` : ''}`}
        >
          {day.day.slice(8)}
        </span>
      ))}
    </div>
  );
}

/**
 * Group rows by TARGET-project — the board's PRIMARY grouping (K3, and K4 /
 * CTO remap 2026-10-12: the project belongs to the TARGET, so a job whose
 * targets span projects appears in EACH project section, its row carrying the
 * section's targets as a muted chip). Pinned order (K4): ismart, kpk,
 * code-of-future, cb-agreements, cb-manager, partner-cabinet, llm-router,
 * online-school, cloudflare, infra, monitor; anything else under `other`. The
 * zone stays visible as the per-row badge; the zone tally lives in the header
 * strip.
 */
export function groupByTargetProject(jobs: OverviewJob[]): Array<{ project: string; jobs: OverviewJob[] }> {
  const buckets = new Map<string, OverviewJob[]>();
  for (const job of jobs) {
    const projects = job.projects?.length ? job.projects : [job.project || OTHER_PROJECT];
    for (const project of projects) {
      const list = buckets.get(project) ?? [];
      list.push(job);
      buckets.set(project, list);
    }
  }
  const pinned = PROJECT_ORDER.filter((project) => buckets.has(project)).map((project) => ({
    project,
    jobs: buckets.get(project) ?? [],
  }));
  const others = [...buckets.keys()]
    .filter((project) => !PROJECT_ORDER.includes(project))
    .sort()
    .map((project) => ({ project, jobs: buckets.get(project) ?? [] }));
  return [...pinned, ...others];
}

/** Short label for a target chip on a spanning row (`platform-ismart/kpk_prod` -> `kpk_prod`). */
export function shortTargetName(name: string): string {
  return name.includes('/') ? name.slice(name.lastIndexOf('/') + 1) : name;
}

/** One board row per job per project, grouped under project headers (K3/K4). */
export function JobBoard({ jobs }: { jobs: OverviewJob[] }) {
  if (jobs.length === 0) {
    return <EmptyState title="Реестр пуст" hint="Джобы появятся после первого seed реестра." />;
  }
  const groups = groupByTargetProject(jobs);
  return (
    <div className="table-wrap">
      <table className="data" data-testid="job-board">
        <thead>
          <tr>
            <th>Джоба</th>
            <th>Где</th>
            <th>Cron</th>
            <th>Статус</th>
            <th className="num">Свежесть</th>
            <th className="num">Прошлый прогон</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <GroupRows key={group.project} project={group.project} jobs={group.jobs} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupRows({ project, jobs }: { project: string; jobs: OverviewJob[] }) {
  // Anything outside the pinned order shares the neutral `other` styling —
  // unknown projects render fine, just without bespoke styling and with the
  // raw name as the header.
  const other = !PROJECT_ORDER.includes(project);
  const title = PROJECT_META[project]?.title ?? project;
  return (
    <>
      <tr className="zone-row">
        <td colSpan={7} title={`проект: ${title}`}>
          <span className={`project-badge project-badge--${other ? 'other' : project}`}>{project}</span>
          <span className="zone-row__title">{title}</span>
          <span className="zone-row__count">{jobs.length}</span>
        </td>
      </tr>
      {jobs.map((job) => (
        <JobRow key={`${project}:${job.id}`} job={job} project={project} />
      ))}
    </>
  );
}

function JobRow({ job, project }: { job: OverviewJob; project: string }) {
  // A job spanning projects repeats per section — the chip says which of its
  // targets THIS section is about, so the repeat reads as attribution, not a bug.
  const slice = (job.projects?.length ?? 0) > 1 ? (job.projectTargets?.[project] ?? []) : [];
  return (
    <tr className={job.suspended ? 'row--suspended' : undefined}>
      <td className="cell-primary">
        <Link to={`/jobs/${job.id}`}>{job.name}</Link>
        <ZoneBadge zone={job.zone} />
        {slice.length > 0 ? (
          <span
            className="muted"
            title={`таргеты этого проекта: ${slice.join(', ')} (K4: проект принадлежит таргету, джоба повторяется в каждом своём проекте)`}
          >
            {slice.map((name) => shortTargetName(name)).join(', ')}
          </span>
        ) : null}
        {job.suspended ? (
          <Badge
            tone="neutral"
            title={job.suspendedReason ?? 'джоба снята с ручника намеренно — вне overall и алертов'}
          >
            suspended
          </Badge>
        ) : null}
        {job.alertsMuted ? (
          <Badge tone="neutral" title="alerts:false — пишется, не алертит">
            muted
          </Badge>
        ) : null}
        {!job.instrumented ? (
          <Badge tone="warn" title="коллектор ещё не получал событий от этой джобы">
            no events
          </Badge>
        ) : null}
      </td>
      <td className="cell-muted">{job.where}</td>
      <td className="cell-muted nowrap">{job.cron ?? '—'}</td>
      <td>
        <StatusPill status={job.status.status} title={job.status.reason} />
      </td>
      <td className="num cell-muted" title={job.status.reason}>
        {formatAge(job.fresh_age_s)}
      </td>
      <td className="num cell-muted" title={job.last_run ? `${job.last_run.run} · ${job.last_run.status}` : 'нет прогонов'}>
        {job.last_run ? (
          <>
            {(job.last_run.uploaded ?? 0) + (job.last_run.skipped ?? 0) + (job.last_run.failed ?? 0)}/{job.last_run.expected_count}{' '}
            <StatusPill status={job.last_run.status} />
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="row-actions">
        <Link className="btn btn--sm btn--secondary" to={`/jobs/${job.id}`}>
          Детали
        </Link>
      </td>
    </tr>
  );
}

/** Per-bucket chips + lag (the sync board v0 view). */
export function SyncPanel({ sync }: { sync: OverviewView['sync'] }) {
  // K4: a switched-off sweep is not "not yet computed" — it is retired on
  // purpose, and the panel must say so instead of showing a stale verdict.
  if (sync.state === 'disabled') {
    return (
      <EmptyState
        title="Синк RU→KZ отключён"
        hint="RU-источник выведен 2026-10-11 — сравнивать не с чем, KZ-копии основные (K4, CTO)."
      />
    );
  }
  if (sync.buckets.length === 0) {
    return <EmptyState title="Синк ещё не считался" hint="Борд наполняется после первого зеркального окна." />;
  }
  return (
    <div className="chip-row" data-testid="sync-panel">
      {sync.buckets.map((bucket) => (
        <SyncChip key={bucket.bucket} bucket={bucket} />
      ))}
    </div>
  );
}

function SyncChip({ bucket }: { bucket: SyncBucketState }) {
  const label =
    bucket.state === 'converged'
      ? 'ok'
      : bucket.state === 'in_flight'
        ? 'sync…'
        : bucket.state === 'overdue'
          ? 'overdue'
          : bucket.state === 'diverged'
            ? 'diverged'
            : 'unknown';
  // Mirror lag is the number an operator scans for; the kz-only flag is the
  // red-flag class (KZ holds objects RU lost — the accidental-deletion echo).
  const lag =
    bucket.kzEmpty === true
      ? 'KZ пуст'
      : bucket.kzLagHours !== null
        ? `lag ${bucket.kzLagHours.toFixed(1)} ч`
        : null;
  const detail = [
    bucket.countDiff !== null ? `diff ${bucket.countDiff}` : null,
    bucket.sizeDiffBytes !== null && bucket.sizeDiffBytes !== 0 ? `size Δ ${formatBytes(bucket.sizeDiffBytes)}` : null,
    lag,
    bucket.reason ?? bucket.error ?? null,
    bucket.kzOnlySample?.length ? `kz-only: ${bucket.kzOnlySample.join(', ')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const alarmed = bucket.state !== 'converged' && bucket.state !== 'unknown' && bucket.state !== 'in_flight';
  return (
    <span
      className={`chip ${bucket.state === 'converged' ? 'chip--up' : alarmed || bucket.kzOnly ? 'chip--down' : ''}`}
      title={`${bucket.bucket} [${bucket.mirrorJob ?? 'mirror'}]: ${bucket.state}${detail ? ` (${detail})` : ''}`}
    >
      <span className="chip__dot" aria-hidden="true" />
      {bucket.bucket}
      {label !== 'ok' ? ` · ${label}` : ''}
      {bucket.kzOnly ? ' · kz-only' : ''}
      {bucket.truncated ? ' · >100k' : ''}
      {lag ? <span className="chip__lag">{` ${lag}`}</span> : null}
    </span>
  );
}

/** Active alerts panel — what needs a human right now. */
export function ActiveAlerts({ data }: { data: AlertsView }) {
  if (data.active.length === 0) {
    return <EmptyState title="Активных алертов нет" hint="Instant-алерты появляются здесь сразу." />;
  }
  const severityOf = (key: string): string => data.history.find((entry) => entry.key === key && !entry.resolved)?.severity ?? 'critical';
  return (
    <div data-testid="active-alerts">
      {data.active.map((key) => (
        <div key={key} className={`banner ${severityOf(key) === 'warning' ? 'banner--warn' : 'banner--bad'}`}>
          <strong>{key}</strong>
          <span>{data.history.find((entry) => entry.key === key && !entry.resolved)?.message ?? 'см. Slack'}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * `/` — the overview board. Auto-refetch 30 s; `placeholderData` keeps the
 * previous rows on screen while a refetch is in flight (no flash).
 */
export function OverviewPage() {
  const board = useQuery({
    queryKey: ['overview'],
    queryFn: ({ signal }) => overview.get(signal),
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });

  const activeAlerts = useQuery({
    queryKey: ['alerts'],
    queryFn: ({ signal }) => alertsApi.get(signal),
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Status</h1>
          <p className="page-header__sub">Ночная волна одним экраном: свежесть, прогоны, синк, алерты.</p>
        </div>
      </div>

      {board.isPending ? <Spinner label="Загружаю борд" /> : null}
      {board.isError ? <ErrorState message={String(board.error)} onRetry={() => void board.refetch()} /> : null}
      {board.data ? (
        <>
          <HeaderStrip data={board.data} />
          <MetricsStrip metrics={board.data.metrics} />

          <section className="card card--flush">
            <h2 className="section-title" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
              Джобы ({board.data.jobs.length})
            </h2>
            <JobBoard jobs={board.data.jobs} />
          </section>

          <section className="card">
            <h2 className="section-title">Активные алерты</h2>
            {activeAlerts.isPending ? <Spinner label="Загружаю алерты" /> : null}
            {activeAlerts.data ? <ActiveAlerts data={activeAlerts.data} /> : null}
            {activeAlerts.isError ? <ErrorState message={String(activeAlerts.error)} onRetry={() => void activeAlerts.refetch()} /> : null}
          </section>

          <section className="card">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
              <h2 className="section-title">Restore-verify (mongo-verify, 7 дней)</h2>
              <Link to="/metrics" className="muted">
                все метрики →
              </Link>
            </div>
            <VerifyBoard board={board.data.metrics.restore_verify.board} job={board.data.metrics.restore_verify.job} />
          </section>

          <section className="card">
            <h2 className="section-title">
              Зеркало RU→KZ
              {board.data.sync.state === 'disabled' ? <span className="muted"> — отключено (RU выведен)</span> : null}
            </h2>
            <SyncPanel sync={board.data.sync} />
          </section>

          <section className="card">
            <h2 className="section-title">Занятость бакетов vs квота</h2>
            <UsageBars usage={board.data.metrics.bucket_usage} />
          </section>
        </>
      ) : null}
    </>
  );
}
